import { memo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { isLosslessNumber } from "lossless-json";
import { toast } from "sonner";
import { childPath } from "./jsonPath";

type Open = { all: number; depth: number }; // bumping `all` re-applies depth to every node

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v) && !isLosslessNumber(v);

function Leaf({ v }: { v: unknown }) {
  if (isLosslessNumber(v)) return <span className="text-sky-600 dark:text-sky-400">{v.toString()}</span>;
  if (typeof v === "string") return <span className="text-emerald-600 dark:text-emerald-400 break-all">{JSON.stringify(v)}</span>;
  if (typeof v === "boolean") return <span className="text-purple-600 dark:text-purple-400">{String(v)}</span>;
  return <span className="text-muted-foreground">null</span>;
}

const Node = memo(function Node({ name, value, path, level, open }: { name: string | null; value: unknown; path: string; level: number; open: Open }) {
  const branch = Array.isArray(value) || isObj(value);
  // `open.all` in the key remounts nodes when Expand / Collapse all is pressed.
  return <NodeInner key={open.all} name={name} value={value} path={path} level={level} open={open} branch={branch} />;
});

function NodeInner({ name, value, path, level, open, branch }: { name: string | null; value: unknown; path: string; level: number; open: Open; branch: boolean }) {
  const [expanded, setExpanded] = useState(level < open.depth);
  const copy = () => { navigator.clipboard.writeText(path).then(() => toast.success(`Copied ${path}`), () => {}); };
  const label = name !== null && (
    <button type="button" onClick={copy} title={`Copy path ${path}`} className="text-foreground hover:underline mr-1">{name}<span className="text-muted-foreground">:</span></button>
  );
  if (!branch) return <div className="pl-4 py-px">{label}<Leaf v={value} /></div>;
  const arr = Array.isArray(value);
  const entries: [string | number, unknown][] = arr ? (value as unknown[]).map((x, i) => [i, x]) : Object.entries(value as object);
  return (
    <div className="pl-4">
      <div className="flex items-center py-px">
        <button type="button" onClick={() => setExpanded(!expanded)} className="mr-1 text-muted-foreground" aria-label={expanded ? "Collapse" : "Expand"} aria-expanded={expanded}>
          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
        {label}
        <span className="text-muted-foreground">{arr ? "[" : "{"}{!expanded && ` ${entries.length} ${entries.length === 1 ? "item" : "items"} ${arr ? "]" : "}"}`}</span>
      </div>
      {expanded && (
        <>
          {entries.map(([k, v]) => <Node key={k} name={arr ? String(k) : (k as string)} value={v} path={childPath(path, k)} level={level + 1} open={open} />)}
          <div className="pl-4 text-muted-foreground">{arr ? "]" : "}"}</div>
        </>
      )}
    </div>
  );
}

export function JsonTree({ value }: { value: unknown }) {
  const [open, setOpen] = useState<Open>({ all: 0, depth: 2 });
  return (
    <div>
      <div className="flex gap-3 text-xs mb-2">
        <button type="button" className="underline" onClick={() => setOpen({ all: open.all + 1, depth: 99 })}>Expand all</button>
        <button type="button" className="underline" onClick={() => setOpen({ all: open.all + 1, depth: 1 })}>Collapse all</button>
        <span className="text-muted-foreground">Click a key to copy its path</span>
      </div>
      <div className="rounded-md border bg-muted/20 p-3 font-mono text-xs max-h-[420px] overflow-auto -ml-4">
        <Node name={null} value={value} path="$" level={0} open={open} />
      </div>
    </div>
  );
}
